import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import useTokenStore from '../../stores/tokens';
import { compatApi } from '../../utils/Intercepter';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import { useIsMobile } from '../../utils/responsive';

const REPORT_TYPES = [
    { value: 'weekly',  label: '주간 업무 보고서', short: '주간',   color: '#7C6BD9' },
    { value: 'daily',   label: '일일 업무 보고서', short: '일일',   color: '#5BC2B2' },
    { value: 'monthly', label: '월간 업무 보고서', short: '월간',   color: '#5d8fe0' },
    { value: 'project', label: '프로젝트 결과 보고서', short: '프로젝트', color: '#e89c43' },
];
function typeMeta(t) { return REPORT_TYPES.find(x => x.value === t) || REPORT_TYPES[0]; }

function fmtDate(iso) {
    if (!iso) return '—';
    return format(new Date(iso), 'yyyy.MM.dd');
}
function docNo(id, writtenAt) {
    try {
        const y = new Date(writtenAt).getFullYear();
        const tail = String(id).replace(/\D/g, '').slice(-4).padStart(4, '0');
        return `RP-${y}-${tail}`;
    } catch { return String(id); }
}

function StatusBadge({ r, tab, myEmail }) {
    const isDraft = r.status === 'draft';
    const myRec = r.receivers?.find(rc => rc.email === myEmail);
    if (isDraft) return <Dot label="임시저장" color="#8b8a99" bg="#f4f3f9" dot="#aaa9b7"/>;
    if (tab === 'received') {
        if (myRec?.readYn === 'N') return <Dot label="미열람" color="#5d8fe0" bg="rgba(93,143,224,.12)" dot="#5d8fe0"/>;
        if (myRec?.commentText) return <Dot label="코멘트 완료" color={MINT} bg={hexToRgba(MINT,.14)} dot={MINT}/>;
        return <Dot label="열람" color="#8b8a99" bg="#f4f3f9" dot="#aaa9b7"/>;
    }
    const allRead = r.totalReceivers > 0 && r.readCount === r.totalReceivers;
    if (r.commentCount > 0) return <Dot label="코멘트 도착" color={MINT} bg={hexToRgba(MINT,.14)} dot={MINT}/>;
    if (allRead) return <Dot label="전원 열람" color="#5BC2B2" bg="rgba(91,194,178,.14)" dot="#5BC2B2"/>;
    if (r.readCount > 0) return <Dot label="일부 열람" color="#e89c43" bg="rgba(232,156,67,.14)" dot="#e89c43"/>;
    return <Dot label="발송" color={PURPLE} bg={hexToRgba(PURPLE,.12)} dot={PURPLE}/>;
}
function Dot({ label, color, bg, dot }) {
    return (
        <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11.5, fontWeight:600, color, background:bg, padding:'3px 9px', borderRadius:999, whiteSpace:'nowrap' }}>
            <span style={{ width:5, height:5, borderRadius:'50%', background:dot }}/>
            {label}
        </span>
    );
}

function StatTile({ label, value, accent, icon, highlight, onClick, active }) {
    return (
        <div 
            onClick={onClick}
            style={{ 
                background:'white', borderRadius:16, padding:'16px 18px', 
                border: active ? `2px solid ${accent}` : highlight ? `1.5px solid ${hexToRgba(accent,.35)}` : '1px solid #f0eff5', 
                boxShadow:'0 1px 2px rgba(60,50,120,.04)', display:'flex', alignItems:'center', gap:14,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 120ms',
            }}
            onMouseEnter={e => { if (onClick) e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { if (onClick) e.currentTarget.style.transform = 'none'; }}
        >
            <div style={{ width:38, height:38, borderRadius:10, background:hexToRgba(accent,.12), color:accent, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{icon}</div>
            <div style={{ display:'flex', flexDirection:'column', lineHeight:1.2, minWidth:0 }}>
                <span style={{ fontSize:11.5, color:'#8b8a99', fontWeight:500 }}>{label}</span>
                <span style={{ fontSize:22, fontWeight:700, color:'#1f1d2e', fontVariantNumeric:'tabular-nums', marginTop:4 }}>{value}<span style={{ fontSize:12, color:'#8b8a99', fontWeight:500, marginLeft:3 }}>건</span></span>
            </div>
        </div>
    );
}

function Pagination({ page, totalPages, onChange }) {
    const win = 2;
    let start = Math.max(1, page - win), end = Math.min(totalPages, page + win);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    const btn = (label, val, disabled, active) => (
        <button key={label+val} disabled={disabled} onClick={() => onChange(val)} style={{ minWidth:30, height:30, padding:'0 8px', background: active ? `linear-gradient(135deg,${PURPLE},${MINT})` : 'white', border: active ? 'none' : '1px solid #ececf2', color: active ? 'white' : disabled ? '#c7c5d2' : '#5d5b6e', borderRadius:7, fontSize:12.5, fontWeight: active ? 600 : 500, cursor: disabled ? 'not-allowed' : 'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}>{label}</button>
    );
    return (
        <div style={{ display:'flex', gap:4 }}>
            {btn('«',1,page===1)}{btn('‹',Math.max(1,page-1),page===1)}
            {start>1&&<span style={{alignSelf:'center',color:'#aaa9b7',padding:'0 4px'}}>…</span>}
            {pages.map(p=>btn(p,p,false,p===page))}
            {end<totalPages&&<span style={{alignSelf:'center',color:'#aaa9b7',padding:'0 4px'}}>…</span>}
            {btn('›',Math.min(totalPages,page+1),page===totalPages)}{btn('»',totalPages,page===totalPages)}
        </div>
    );
}

const PAGE_SIZE = 10;

export default function ReportListPage() {
    const isMobile = useIsMobile(768);
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useTokenStore();
    const myEmail = user?.userEmail;
    const orgCode = user?.currentOrganizationCode;

    const [tab, setTab] = useState(() => location.state?.tab || 'received');
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState(() => location.state?.statusFilter || '전체');

    useEffect(() => {
        if (location.state?.tab) {
            setTab(location.state.tab);
        }
        if (location.state?.statusFilter) {
            setStatusFilter(location.state.statusFilter);
        }
    }, [location.state]);
    const [page, setPage] = useState(1);

    const endpointTab = (tab === 'sent' || tab === 'draft') ? 'sent' : 'received';

    const fetchReports = useCallback(() => {
        if (!orgCode) return;
        setLoading(true);
        const endpoint = endpointTab === 'received' ? '/report/received' : '/report/sent';
        compatApi.get(endpoint, { params: { orgCode, type: typeFilter || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, q: q || undefined } })
            .then(res => setReports(res.data || []))
            .catch(() => setReports([]))
            .finally(() => setLoading(false));
    }, [orgCode, endpointTab, typeFilter, dateFrom, dateTo, q]);

    useEffect(() => { fetchReports(); }, [fetchReports]);
    useEffect(() => { setPage(1); }, [tab, typeFilter, statusFilter, dateFrom, dateTo, q]);
    useEffect(() => { setStatusFilter('전체'); }, [tab]);

    const tabReports = useMemo(() => {
        if (tab === 'received') return reports;
        if (tab === 'sent') return reports.filter(r => r.status === 'published');
        if (tab === 'draft') return reports.filter(r => r.status === 'draft');
        return reports;
    }, [reports, tab]);

    const filteredReports = useMemo(() => {
        if (statusFilter === '전체') return tabReports;
        if (tab === 'received') {
            return tabReports.filter(r => {
                const myRec = r.receivers?.find(rc => rc.email === myEmail);
                if (statusFilter === '미열람') return myRec?.readYn === 'N';
                if (statusFilter === '코멘트 완료') return !!myRec?.commentText;
                if (statusFilter === '열람') return myRec?.readYn === 'Y' && !myRec?.commentText;
                return true;
            });
        } else if (tab === 'sent') {
            return tabReports.filter(r => {
                const allRead = r.totalReceivers > 0 && r.readCount === r.totalReceivers;
                if (statusFilter === '코멘트 도착') return r.commentCount > 0;
                if (statusFilter === '전원 열람') return allRead;
                if (statusFilter === '일부 미열람') return r.readCount > 0 && r.readCount < r.totalReceivers;
                return true;
            });
        }
        return tabReports;
    }, [tabReports, statusFilter, tab, myEmail]);

    const totalPages = Math.max(1, Math.ceil(filteredReports.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paged = filteredReports.slice((safePage-1)*PAGE_SIZE, safePage*PAGE_SIZE);

    const stats = useMemo(() => {
        const thisYear = String(new Date().getFullYear());
        const yearReports = reports.filter(r => r.writtenAt?.startsWith(thisYear));
        if (tab === 'received') {
            const unread = yearReports.filter(r => r.receivers?.find(rc => rc.email === myEmail)?.readYn === 'N').length;
            const commented = yearReports.filter(r => r.receivers?.find(rc => rc.email === myEmail)?.commentText).length;
            const read = yearReports.filter(r => { const me = r.receivers?.find(rc => rc.email === myEmail); return me?.readYn === 'Y' && !me?.commentText; }).length;
            return [
                { label:'전체 받은 보고서', value:yearReports.length, accent:PURPLE, filterValue: '전체' },
                { label:'미열람', value:unread, accent:'#5d8fe0', highlight:unread>0, filterValue: '미열람' },
                { label:'코멘트 완료', value:commented, accent:MINT, filterValue: '코멘트 완료' },
                { label:'열람 완료', value:read, accent:'#8b8a99', filterValue: '열람' },
            ];
        } else if (tab === 'sent') {
            const sentList = yearReports.filter(r => r.status === 'published');
            const commented = sentList.filter(r => r.commentCount > 0).length;
            const allRead = sentList.filter(r => r.readCount === r.totalReceivers && r.totalReceivers > 0).length;
            const unread = sentList.filter(r => r.readCount < r.totalReceivers).length;
            return [
                { label:'전체 보낸 보고서', value:sentList.length, accent:PURPLE, filterValue: '전체' },
                { label:'코멘트 도착', value:commented, accent:MINT, highlight:commented>0, filterValue: '코멘트 도착' },
                { label:'전원 열람', value:allRead, accent:'#5BC2B2', filterValue: '전원 열람' },
                { label:'미열람 존재', value:unread, accent:'#e89c43', highlight:unread>0, filterValue: '일부 미열람' },
            ];
        } else {
            const drafts = yearReports.filter(r => r.status === 'draft');
            const weeklyDrafts = drafts.filter(r => r.type === 'weekly').length;
            const dailyDrafts = drafts.filter(r => r.type === 'daily').length;
            const otherDrafts = drafts.filter(r => r.type !== 'weekly' && r.type !== 'daily').length;
            return [
                { label:'전체 임시저장', value:drafts.length, accent:'#8b8a99' },
                { label:'주간 보고서', value:weeklyDrafts, accent:'#7C6BD9' },
                { label:'일일 보고서', value:dailyDrafts, accent:'#5BC2B2' },
                { label:'기타 보고서', value:otherDrafts, accent:'#5d8fe0' },
            ];
        }
    }, [reports, tab, myEmail]);

    const statusPills = tab === 'received'
        ? ['전체','미열람','코멘트 완료','열람']
        : tab === 'sent'
            ? ['전체','코멘트 도착','전원 열람','일부 미열람']
            : ['전체'];

    const statIcons = [
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h10M7 17h6"/></svg>,
        tab === 'received'
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16v12H4z"/><path d="M4 7l8 7 8-7"/></svg>
            : tab === 'sent'
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
        tab === 'draft'
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="11" y2="17"/></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7"/></svg>,
        tab === 'received'
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
            : tab === 'sent'
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
    ];

    return (
        <div style={{ minHeight:'100vh', background:'#f5f4fa' }}>
            <main style={{ maxWidth:1600, margin:'0 auto', padding:'32px clamp(20px,3vw,40px) 48px' }}>
                {/* Header */}
                <div style={{ marginBottom:20 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, fontSize:12, color:'#8b8a99' }}>
                        <span>보고서</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform:'rotate(-90deg)' }}><polyline points="6 9 12 15 18 9"/></svg>
                        <span style={{ color:'#3a3849', fontWeight:500 }}>{tab === 'received' ? '받은 보고서' : '보낸 보고서'}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:16, flexWrap:'wrap' }}>
                        <div>
                            <h1 style={{ margin:0, fontSize:24, fontWeight:700, color:'#1f1d2e', letterSpacing:'-0.01em' }}>보고서</h1>
                            <p style={{ margin:'4px 0 0', fontSize:13.5, color:'#7a7989' }}>작성·수신한 보고서를 한 곳에서 확인하세요.</p>
                        </div>
                        <button onClick={() => navigate('/report/create')} style={{ height:38, padding:'0 18px', borderRadius:10, background:`linear-gradient(135deg,${PURPLE},${MINT})`, border:'none', color:'white', fontSize:13, fontWeight:600, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6, boxShadow:`0 6px 14px -6px ${hexToRgba(PURPLE,.55)}` }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                            새 보고서 작성
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div style={{ display:'inline-flex', gap:4, background:'white', padding:4, borderRadius:12, border:'1px solid #ececf2', boxShadow:'0 1px 2px rgba(60,50,120,.04)', marginBottom:14 }}>
                    {[{value:'received',label:'받은 보고서'},{value:'sent',label:'보낸 보고서'},{value:'draft',label:'임시 저장함'}].map(t => {
                        const active = tab === t.value;
                        return (
                            <button key={t.value} onClick={() => setTab(t.value)} style={{ height:34, padding:'0 16px', borderRadius:8, border:'none', background: active ? `linear-gradient(135deg,${PURPLE},${MINT})` : 'transparent', color: active ? 'white' : '#5d5b6e', fontSize:13, fontWeight: active ? 600 : 500, cursor:'pointer', transition:'all 140ms', display:'inline-flex', alignItems:'center', gap:7 }}>
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* Stats */}
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap:14, marginBottom:16 }}>
                    {stats.map((s,i) => (
                        <StatTile 
                            key={s.label} 
                            {...s} 
                            icon={statIcons[i]}
                            active={s.filterValue && statusFilter === s.filterValue}
                            onClick={s.filterValue ? () => setStatusFilter(s.filterValue) : undefined}
                        />
                    ))}
                </div>

                {/* Table card */}
                <div style={{ background:'white', borderRadius:16, border:'1px solid #f0eff5', boxShadow:'0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)', overflow:'hidden' }}>
                    {/* Status pills */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px', background:'linear-gradient(180deg,#faf9fd 0%,#f5f4fa 100%)', borderBottom:'1px solid #ececf2', gap:12, flexWrap:'wrap' }}>
                        <div style={{ display:'inline-flex', gap:4, background:'white', padding:3, borderRadius:10, border:'1px solid #ececf2' }}>
                            {statusPills.map(opt => {
                                const active = statusFilter === opt;
                                return <button key={opt} onClick={() => setStatusFilter(opt)} style={{ height:28, padding:'0 12px', borderRadius:7, border:'none', background: active ? `linear-gradient(135deg,${PURPLE},${MINT})` : 'transparent', color: active ? 'white' : '#5d5b6e', fontSize:12, fontWeight: active ? 600 : 500, cursor:'pointer' }}>{opt}</button>;
                            })}
                        </div>
                        <span style={{ fontSize:12, color:'#8b8a99' }}>총 {filteredReports.length}건</span>
                    </div>

                    {/* Filters */}
                    <div style={{ padding:'12px 20px', borderBottom:'1px solid #ececf2', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:11, fontWeight:600, color:'#5d5b6e' }}>종류</span>
                            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ background:'white', border:'1px solid #ececf2', borderRadius:8, padding:'0 12px', height:34, fontSize:13, fontFamily:'inherit', outline:'none' }}>
                                <option value="">전체</option>
                                {REPORT_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                            </select>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:11, fontWeight:600, color:'#5d5b6e' }}>작성일</span>
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ background:'white', border:'1px solid #ececf2', borderRadius:8, padding:'0 10px', height:34, fontSize:13, fontFamily:'inherit', outline:'none', width:140 }}/>
                            <span style={{ color:'#8b8a99', fontSize:13 }}>~</span>
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ background:'white', border:'1px solid #ececf2', borderRadius:8, padding:'0 10px', height:34, fontSize:13, fontFamily:'inherit', outline:'none', width:140 }}/>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8, background:'#f7f6fb', borderRadius:8, padding:'0 12px', height:34, minWidth:220, marginLeft:'auto' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b8a99" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
                            <input value={q} onChange={e => setQ(e.target.value)} placeholder="요약·작성자·주차 검색" style={{ background:'transparent', border:'none', outline:'none', fontSize:13, flex:1, fontFamily:'inherit' }}/>
                        </div>
                    </div>

                    {/* Table */}
                    {loading ? (
                        <div style={{ padding:'60px 20px', textAlign:'center', color:'#8b8a99' }}>불러오는 중...</div>
                    ) : filteredReports.length === 0 ? (
                        <div style={{ padding:'72px 20px', textAlign:'center' }}>
                            <div style={{ fontSize:15, fontWeight:600, color:'#1f1d2e', marginBottom:4 }}>
                                {tab === 'received' ? '받은 보고서가 없어요' : tab === 'sent' ? '작성한 보고서가 없어요' : '임시 저장된 보고서가 없어요'}
                            </div>
                            <div style={{ fontSize:13, color:'#8b8a99' }}>
                                {tab !== 'received' && <>'새 보고서 작성' 버튼으로 시작하세요.</>}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div style={{ overflowX:'auto' }}>
                                <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:0, fontSize:13, color:'#3a3849', minWidth: tab === 'received' ? 1100 : 1220 }}>
                                    <thead>
                                        <tr style={{ background:'#f7f6fb' }}>
                                            {['문서 번호','구분','제목', tab==='received'?'작성자':'수신자', ...(tab!=='received'?['조직']:[]), '작성일','열람','코멘트','상태'].map((h,i) => (
                                                <th key={h} style={{ padding:'12px 12px', fontSize:12, fontWeight:600, color:'#5d5b6e', textAlign: i===2?'left':'center', borderBottom:'1px solid #ececf2', whiteSpace:'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paged.map(r => {
                                            const tm = typeMeta(r.type || 'weekly');
                                            const tdStyle = { padding:'12px 12px', verticalAlign:'middle', borderBottom:'1px solid #f4f3f9', whiteSpace:'nowrap' };
                                            const myRec = r.receivers?.find(rc => rc.email === myEmail);
                                            const isUnread = tab === 'received' && myRec?.readYn === 'N';
                                            return (
                                                <tr key={r.id} onClick={() => navigate(`/report/${r.id}`)} style={{ background:'white', cursor:'pointer', transition:'background 100ms' }}
                                                    onMouseEnter={e => e.currentTarget.style.background='#fafaff'}
                                                    onMouseLeave={e => e.currentTarget.style.background='white'}>
                                                    <td style={{ ...tdStyle, textAlign:'center' }}>
                                                        <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11.5, color:'#8b8a99', fontWeight:600, background:'#f4f3f9', padding:'2px 8px', borderRadius:6 }}>
                                                            {isUnread && <span style={{ width:5, height:5, borderRadius:'50%', background:PURPLE }}/>}
                                                            {docNo(r.id, r.writtenAt)}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign:'center' }}>
                                                        <span style={{ fontSize:11, fontWeight:600, color:tm.color, background:hexToRgba(tm.color,.12), padding:'3px 8px', borderRadius:6 }}>{tm.short}</span>
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign:'left', maxWidth:400 }}>
                                                        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block', maxWidth:'100%' }}>
                                                            <span style={{ color:PURPLE, fontWeight:600, marginRight:6 }}>[{r.reportYear} {r.reportWeek}주차]</span>
                                                            {tm.label}
                                                            {r.summary && <span style={{ color:'#8b8a99', marginLeft:6, fontSize:12 }}>· {r.summary}</span>}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign:'center' }}>
                                                        {tab === 'received' ? (
                                                            <span>{r.authorName} <span style={{ color:'#8b8a99' }}>{r.authorRank}</span></span>
                                                        ) : (
                                                            <span>
                                                                {r.receivers?.[0]?.name || '—'}{r.receivers?.[0]?.rank ? ` ${r.receivers[0].rank}` : ''}
                                                                {r.receivers?.length > 1 && <span style={{ color:'#8b8a99', fontSize:12 }}> 외 {r.receivers.length-1}명</span>}
                                                            </span>
                                                        )}
                                                    </td>
                                                    {tab !== 'received' && (
                                                        <td style={{ ...tdStyle, textAlign:'center' }}>
                                                            <span style={{ fontSize:12, fontWeight:500, color:'#5d5b6e' }}>{r.orgName || '—'}</span>
                                                        </td>
                                                    )}
                                                    <td style={{ ...tdStyle, textAlign:'center', fontVariantNumeric:'tabular-nums' }}>{fmtDate(r.writtenAt)}</td>
                                                    <td style={{ ...tdStyle, textAlign:'center' }}>
                                                        <span style={{ fontSize:12, fontWeight:600, color:'#3a3849', background:'#f4f3f9', padding:'3px 9px', borderRadius:6 }}>
                                                            {r.readCount ?? 0}<span style={{ color:'#aaa9b7' }}>/</span>{r.totalReceivers ?? 0}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign:'center' }}>
                                                        {r.commentCount > 0
                                                            ? <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:12, fontWeight:600, color:MINT, background:hexToRgba(MINT,.14), padding:'3px 9px', borderRadius:6 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>{r.commentCount}</span>
                                                            : <span style={{ color:'#c7c5d2' }}>—</span>}
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign:'center' }}>
                                                        <StatusBadge r={r} tab={tab} myEmail={myEmail}/>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ padding:'12px 20px', borderTop:'1px solid #ececf2', background:'#fafaff', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                                <span style={{ fontSize:12, color:'#8b8a99' }}>전체 <strong style={{ color:'#1f1d2e' }}>{filteredReports.length}</strong>건 · {(safePage-1)*PAGE_SIZE+1}-{Math.min(safePage*PAGE_SIZE, filteredReports.length)} 표시</span>
                                <Pagination page={safePage} totalPages={totalPages} onChange={setPage}/>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
